import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Briefcase,
  CalendarClock,
  ChevronLeft,
  ExternalLink,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Receipt,
  Send,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { KpiCard, MoneyAmount, PageContainer, SectionTitle } from '@/components/design-system';
import { useTier } from '@/hooks/queries/useTiers';
import { useOpportunites } from '@/hooks/queries/useCrmOpportunites';
import { useRecouvrementEntreprises } from '@/hooks/queries/useRecouvrementEntreprises';
import { useRelances } from '@/hooks/queries/useRelances';
import { usePlansPaiement } from '@/hooks/queries/usePlansPaiement';
import { useInscriptions } from '@/hooks/queries/useFormation';
import { formatDA, formatDateDZ } from '@/lib/format';

// Sprint 6.9.17 — Fiche tier 360°.
// Agrège dans une seule vue : opportunités CRM, factures Recouvrement,
// relances, plans de paiement, inscriptions formation pour un tier donné.
// Tous les hooks acceptaient déjà un filtre tier_id depuis longtemps —
// ne manquait que la page qui les compose.

export function TierDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const tierId = id ?? null;

  const { data: tier, isLoading } = useTier(tierId);
  const entrepriseId = tier?.entreprise_id ?? null;

  const { data: oppData } = useOpportunites({ entrepriseId, tierId, pageSize: 50 });
  const { data: factures = [] } = useRecouvrementEntreprises({ entrepriseId, tierId });
  const { data: relances = [] } = useRelances({ entrepriseId, tierId });
  const { data: plans = [] } = usePlansPaiement({ entrepriseId, tierId });
  const inscQuery = useInscriptions({ tierId, pageSize: 50 });
  const inscriptions = useMemo(() => inscQuery.data?.rows ?? [], [inscQuery.data]);
  const opportunites = useMemo(() => oppData?.rows ?? [], [oppData]);

  const kpis = useMemo(() => {
    const caTotal = factures.reduce((a, f) => a + Number(f.montant_ttc), 0);
    const encaisse = factures.reduce((a, f) => a + Number(f.montant_encaisse), 0);
    const enCours = factures
      .filter((f) => f.statut !== 'regle')
      .reduce((a, f) => a + (Number(f.montant_ttc) - Number(f.montant_encaisse)), 0);
    return {
      caTotal,
      encaisse,
      enCours,
      nbFactures: factures.length,
      nbOpps: opportunites.length,
      nbRelances: relances.length,
      nbPlans: plans.filter((p) => p.statut === 'actif' || p.statut === 'en_retard').length,
      nbInscriptions: inscriptions.length,
    };
  }, [factures, opportunites, relances, plans, inscriptions]);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="space-y-3">
          <div className="h-10 w-64 animate-pulse rounded bg-slate-100" />
          <div className="h-32 animate-pulse rounded bg-slate-100" />
        </div>
      </PageContainer>
    );
  }

  if (!tier) {
    return (
      <PageContainer>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
          Tier introuvable.{' '}
          <Link to="/parametres/tiers" className="text-primary hover:underline">
            Retour
          </Link>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/parametres/tiers"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ChevronLeft className="h-3 w-3" /> Retour aux tiers
      </Link>

      {/* Header identité */}
      <header className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] uppercase">
                {tier.type.replace('_', ' ')}
              </Badge>
              {!tier.is_active && (
                <Badge variant="destructive" className="text-[10px]">
                  Inactif
                </Badge>
              )}
              {tier.code && <span className="font-mono text-xs text-slate-500">{tier.code}</span>}
            </div>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">{tier.raison_sociale}</h1>
            {(tier.nom || tier.prenom) && (
              <p className="mt-0.5 text-sm text-slate-600">
                Contact : {tier.prenom ?? ''} {tier.nom ?? ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-slate-600">
            {tier.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> {tier.email}
              </span>
            )}
            {tier.telephone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" /> {tier.telephone}
              </span>
            )}
            {(tier.ville || tier.wilaya) && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {[tier.ville, tier.wilaya].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        </div>
        {(tier.nif || tier.rc) && (
          <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
            {tier.nif && (
              <span>
                <span className="text-slate-500">NIF : </span>
                <span className="font-mono">{tier.nif}</span>
              </span>
            )}
            {tier.rc && (
              <span>
                <span className="text-slate-500">RC : </span>
                <span className="font-mono">{tier.rc}</span>
              </span>
            )}
            {tier.encours_max != null && (
              <span>
                <span className="text-slate-500">Encours max : </span>
                <span className="font-semibold">{formatDA(Number(tier.encours_max))}</span>
              </span>
            )}
          </div>
        )}
      </header>

      {/* KPIs commerciaux */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="CA facturé" value={formatDA(kpis.caTotal)} variant="finances" />
        <KpiCard label="Encaissé" value={formatDA(kpis.encaisse)} variant="formation_b2b" />
        <KpiCard label="En cours" value={formatDA(kpis.enCours)} variant="echeances" />
        <KpiCard
          label="Activité"
          value={`${kpis.nbOpps} opp · ${kpis.nbFactures} fact`}
          variant="crm"
        />
      </div>

      {/* Sections */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section
          icon={<Briefcase className="h-4 w-4 text-blue-600" />}
          title={`Opportunités CRM (${kpis.nbOpps})`}
          empty="Aucune opportunité enregistrée pour ce tier."
          isEmpty={opportunites.length === 0}
        >
          <ul className="space-y-1.5">
            {opportunites.slice(0, 8).map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs"
              >
                <Link
                  to={`/crm/opportunite/${o.id}`}
                  className="flex min-w-0 flex-1 items-center gap-2 hover:underline"
                >
                  <span className="font-mono text-[10px] text-slate-500">{o.reference}</span>
                  <span className="truncate font-medium">{o.titre}</span>
                </Link>
                <span className="num shrink-0 font-semibold">
                  {formatDA(Number(o.montant_estime))}
                </span>
              </li>
            ))}
            {opportunites.length > 8 && (
              <li className="text-[10px] text-muted-foreground">
                +{opportunites.length - 8} autres…
              </li>
            )}
          </ul>
        </Section>

        <Section
          icon={<Receipt className="h-4 w-4 text-emerald-600" />}
          title={`Factures Recouvrement (${kpis.nbFactures})`}
          empty="Aucune facture B2B."
          isEmpty={factures.length === 0}
        >
          <ul className="space-y-1.5">
            {factures.slice(0, 8).map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs"
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-500">
                    {f.reference_facture}
                  </span>
                  <Badge
                    variant={
                      f.statut === 'regle'
                        ? 'default'
                        : f.statut === 'partiel'
                          ? 'secondary'
                          : 'outline'
                    }
                    className="text-[9px]"
                  >
                    {f.statut}
                  </Badge>
                </span>
                <MoneyAmount amount={Number(f.montant_ttc)} variant="inline" />
              </li>
            ))}
            {factures.length > 8 && (
              <li className="text-[10px] text-muted-foreground">+{factures.length - 8} autres…</li>
            )}
          </ul>
        </Section>

        <Section
          icon={<Send className="h-4 w-4 text-amber-600" />}
          title={`Relances (${kpis.nbRelances})`}
          empty="Aucune relance envoyée."
          isEmpty={relances.length === 0}
        >
          <ul className="space-y-1.5">
            {relances.slice(0, 6).map((r) => (
              <li
                key={r.id}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">
                    {r.niveau} <span className="font-normal text-slate-500">· {r.canal}</span>
                  </span>
                  <span className="font-mono text-[10px] text-slate-500">
                    {r.sent_at ? formatDateDZ(r.sent_at.slice(0, 10)) : '—'}
                  </span>
                </div>
                {r.destinataire && (
                  <div className="mt-0.5 text-[11px] text-slate-500">→ {r.destinataire}</div>
                )}
              </li>
            ))}
            {relances.length > 6 && (
              <li className="text-[10px] text-muted-foreground">+{relances.length - 6} autres…</li>
            )}
          </ul>
        </Section>

        <Section
          icon={<CalendarClock className="h-4 w-4 text-violet-600" />}
          title={`Plans de paiement actifs (${kpis.nbPlans})`}
          empty="Aucun plan en cours."
          isEmpty={plans.length === 0}
        >
          <ul className="space-y-1.5">
            {plans.slice(0, 6).map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-2 rounded-md border border-violet-200 bg-violet-50/40 px-2.5 py-1.5 text-xs"
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="font-mono text-[10px] text-violet-900">{p.reference}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {p.statut}
                  </Badge>
                </span>
                <span className="num font-semibold">
                  {p.nb_echeances} × {formatDA(Number(p.montant_total) / p.nb_echeances)}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          icon={<GraduationCap className="h-4 w-4 text-indigo-600" />}
          title={`Inscriptions formation (${kpis.nbInscriptions})`}
          empty="Aucune inscription formation."
          isEmpty={inscriptions.length === 0}
        >
          <ul className="space-y-1.5">
            {inscriptions.slice(0, 8).map((i) => (
              <li
                key={i.id}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs"
              >
                <Link
                  to={`/formation/sessions/${i.session_id}`}
                  className="flex min-w-0 flex-1 items-center gap-2 hover:underline"
                >
                  <span className="font-mono text-[10px] text-slate-500">{i.reference}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {i.statut}
                  </Badge>
                </Link>
                <span className="num shrink-0">{formatDA(Number(i.prix_facture))}</span>
                {i.opportunite_id && (
                  <Link
                    to={`/crm/opportunite/${i.opportunite_id}`}
                    title="Opportunité CRM source"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </li>
            ))}
            {inscriptions.length > 8 && (
              <li className="text-[10px] text-muted-foreground">
                +{inscriptions.length - 8} autres…
              </li>
            )}
          </ul>
        </Section>
      </div>

      {tier.notes && (
        <div className="mt-5 rounded-md border border-slate-200 bg-amber-50/50 p-3 text-xs">
          <div className="font-semibold text-amber-900">Notes internes</div>
          <p className="mt-1 whitespace-pre-wrap text-slate-700">{tier.notes}</p>
        </div>
      )}
    </PageContainer>
  );
}

function Section({
  icon,
  title,
  empty,
  isEmpty,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  empty: string;
  isEmpty: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <SectionTitle>
        <span className="inline-flex items-center gap-2">
          {icon}
          {title}
        </span>
      </SectionTitle>
      <div className="mt-3">
        {isEmpty ? <p className="text-xs text-muted-foreground">{empty}</p> : children}
      </div>
    </section>
  );
}
